import { useQuery } from "@tanstack/react-query"
import { useParams } from "@tanstack/react-router"
import { trpc } from "@/lib/trpc-client"
import { isUuid } from "@/lib/short-id"

// Route params for trips/guides carry a short id in new links and a uuid in
// old ones. Everything below the route (tripKey cache, `trip:<id>` broadcast
// channels, chat routeParams) must see the canonical uuid, so these hooks
// resolve once at the boundary: uuid params pass straight through, short ids
// resolve via tRPC.

function useResolvedId(
  kind: `trip` | `guide`,
  param: string | undefined,
): { id: string | undefined; notFound: boolean } {
  const needsResolve = !!param && !isUuid(param)
  const { data, isError } = useQuery({
    queryKey: [`short-id`, kind, param],
    queryFn: () =>
      kind === `trip`
        ? trpc.trips.resolveShortId.query({ shortId: param ?? `` })
        : trpc.guides.resolveShortId.query({ shortId: param ?? `` }),
    enabled: needsResolve,
    staleTime: Infinity,
  })
  if (!param) return { id: undefined, notFound: false }
  if (!needsResolve) return { id: param, notFound: false }
  if (isError) return { id: undefined, notFound: true }
  if (data === undefined) return { id: undefined, notFound: false }
  return { id: data.id ?? undefined, notFound: data.id === null }
}

// For route components that own a single param. Returns the uuid, `undefined`
// while resolving, `null` if nothing matches (render <NotFound />).
export function useCanonicalId(
  kind: `trip` | `guide`,
  param: string,
): string | null | undefined {
  const { id, notFound } = useResolvedId(kind, param)
  return notFound ? null : id
}

// For components that read trip/guide ids out of whatever route is current
// (chat target, link hovers). Replaces the old raw `useParams` + isUUID reads,
// which treated a short id as "no target" and silently de-scoped the chat.
export function useCanonicalRouteIds(): {
  tripId: string | undefined
  guideId: string | undefined
} {
  const params = useParams({ strict: false }) as {
    tripId?: string
    guideId?: string
  }
  const { id: tripId } = useResolvedId(`trip`, params.tripId)
  const { id: guideId } = useResolvedId(`guide`, params.guideId)
  return { tripId, guideId }
}
